import type { ReactNode } from 'react';

import { Card, cn, Spinner } from '@platform/ui';

/**
 * The small presentational pieces the operations screens share (M11.1–M11.3).
 *
 * They live here rather than in `@platform/ui` because each is one screen's idea so far.
 * Anything a second app wants is promoted to the design system then, not now.
 */

export interface StatTileProps {
  label: string;
  value: ReactNode;
  /** One short line under the number: what it counts, or what to do about it. */
  hint?: ReactNode;
  /** Turns the whole tile into a link target rendered by the caller. */
  action?: ReactNode;
  /** Draws attention when the number is not zero. Never the only signal. */
  tone?: 'default' | 'warning' | 'danger';
}

/** The KPI card of the tokens doc §10.4: label, 36px number, one line of context. */
export function StatTile({ label, value, hint, action, tone = 'default' }: StatTileProps) {
  return (
    <Card className="flex h-full flex-col justify-between p-5">
      <div>
        <p className="text-2xs font-medium tracking-badge text-neutral-400-text uppercase">
          {label}
        </p>
        <p
          className={cn(
            'mt-2 text-4xl font-bold tabular-nums',
            tone === 'danger'
              ? 'text-danger-ink'
              : tone === 'warning'
                ? 'text-warning-ink'
                : 'text-ink',
          )}
        >
          {value}
        </p>
        {hint === undefined ? null : <p className="mt-1 text-sm text-neutral-500">{hint}</p>}
      </div>
      {action === undefined ? null : <div className="mt-4">{action}</div>}
    </Card>
  );
}

export interface DataListItem {
  label: string;
  value: ReactNode;
}

export interface DataListProps {
  items: DataListItem[];
  /** Two columns from 640px; one column below, which is the phone layout. */
  columns?: 1 | 2;
  className?: string;
}

/**
 * The `/payment` `.info-grid` pattern (tokens doc §10.3): a real `<dl>` of label–value
 * pairs, used by every detail card so a record always reads the same way.
 */
export function DataList({ items, columns = 2, className }: DataListProps) {
  return (
    <dl
      className={cn(
        'grid gap-x-6 gap-y-4',
        columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1',
        className,
      )}
    >
      {items.map((item) => (
        <div key={item.label} className="min-w-0">
          <dt className="text-2xs font-medium tracking-badge text-neutral-400-text uppercase">
            {item.label}
          </dt>
          <dd className="mt-[3px] text-base break-words text-ink">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A loading block the same height as the content it replaces, so nothing jumps. */
export function LoadingBlock({ label, rows = 3 }: { label: string; rows?: number }) {
  return (
    <div className="px-6 py-8">
      <Spinner label={label} />
      <div className="mt-5 flex flex-col gap-3" aria-hidden="true">
        {Array.from({ length: rows }, (_, index) => (
          <div key={index} className="h-4 rounded-sm bg-neutral-100" />
        ))}
      </div>
    </div>
  );
}
